import { useCallback, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useIngestionDetail } from '@/shared/hooks/api/ingestionDiagnostics'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  IngestionPageInspector,
  IngestionTimeline,
} from '../components/IngestionTimeline'
import { statusBadgeVariant } from '../utils/formatDiagnosticsTime'

export default function IngestionDetailPage() {
  const { sourceId } = useParams<{ sourceId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedPage = Number(searchParams.get('page') ?? '')
  const { data: session, isFetched: isSessionFetched } = useGetUserSession()

  const detailQuery = useIngestionDetail(sourceId, {
    enabled: isSessionFetched && Boolean(session?.isArtemAdmin),
  })

  const detail = detailQuery.data
  const pages = detail?.rag.ocrJob?.pages ?? []
  const sourceUrl =
    detail?.rag.artifacts?.source_url ?? detail?.lexSignedSourceUrl ?? null
  const selectedPageMeta = pages.find((page) => page.page_number === selectedPage)

  const sortedPageNumbers = useMemo(
    () => [...pages].sort((left, right) => left.page_number - right.page_number).map((page) => page.page_number),
    [pages],
  )

  const adjacentPageNumbers = useMemo(() => {
    const selectedIndex = sortedPageNumbers.indexOf(selectedPage)
    if (selectedIndex === -1) {
      return { prev: undefined, next: undefined }
    }
    return {
      prev: sortedPageNumbers[selectedIndex - 1],
      next: sortedPageNumbers[selectedIndex + 1],
    }
  }, [selectedPage, sortedPageNumbers])

  const selectPage = useCallback(
    (pageNumber: number) => {
      const next = new URLSearchParams(searchParams)
      next.set('page', String(pageNumber))
      setSearchParams(next)
    },
    [searchParams, setSearchParams],
  )

  const showSelectedPageInspector =
    Number.isFinite(selectedPage) && selectedPage > 0 && sourceId != null

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-auto p-4 sm:p-6'>
      <div className='mx-auto flex w-full max-w-7xl flex-col gap-6'>
      <div className='flex items-center gap-3'>
        <Button variant='ghost' size='sm' asChild>
          <Link to='/admin/diagnostics/ingestions'>← Back to list</Link>
        </Button>
      </div>

      {detailQuery.isLoading && (
        <p className='text-sm text-muted-foreground'>Loading ingestion detail…</p>
      )}

      {detail && (
        <>
          <header className='space-y-3 rounded-lg border bg-card p-4'>
            <div className='flex flex-wrap items-start justify-between gap-3'>
              <div>
                <h1 className='text-xl font-semibold'>{detail.source.name}</h1>
                <p className='text-sm text-muted-foreground'>
                  {detail.source.organization.name}
                  {' · '}
                  {detail.source.origin === 'packaging' &&
                  detail.source.packagingItem ? (
                    <Link
                      className='text-primary hover:underline'
                      to={`/packaging-suppliers/${detail.source.packagingItem.packagingSupplierId}/items/${detail.source.packagingItem.id}`}
                    >
                      {detail.source.packagingItem.name} (
                      {detail.source.packagingItem.packagingSupplierName})
                    </Link>
                  ) : (
                    <>
                      {detail.source.product?.name ??
                        detail.source.product?.id ??
                        'Product'}
                    </>
                  )}
                </p>
              </div>
              <Badge
                variant={statusBadgeVariant(detail.source.ingestionStatus ?? '')}
              >
                {detail.source.ingestionStatus ?? '—'}
              </Badge>
            </div>

            <dl className='grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3'>
              <div>
                <dt className='text-muted-foreground'>Source id</dt>
                <dd className='font-mono text-xs'>{detail.source.id}</dd>
              </div>
              <div>
                <dt className='text-muted-foreground'>RAG job id</dt>
                <dd className='font-mono text-xs'>{detail.source.ragJobId ?? '—'}</dd>
              </div>
              <div>
                <dt className='text-muted-foreground'>File upload status</dt>
                <dd>{detail.source.fileUploadStatus ?? '—'}</dd>
              </div>
              <div>
                <dt className='text-muted-foreground'>Stage</dt>
                <dd>{detail.source.ingestionStage ?? '—'}</dd>
              </div>
            </dl>

            {detail.rag.statusComparison && (
              <div
                className={`rounded-md border px-3 py-2 text-sm ${
                  detail.rag.statusComparison.mismatched
                    ? 'border-[#ffb800]/40 bg-[#fff8e6] text-[#cc9300]'
                    : 'border-border bg-muted/30'
                }`}
              >
                Status — Lex: {detail.rag.statusComparison.lex}
                {detail.rag.statusComparison.ingestion &&
                  ` · RAG ingestion: ${detail.rag.statusComparison.ingestion}`}
                {detail.rag.statusComparison.ocr &&
                  ` · OCR: ${detail.rag.statusComparison.ocr}`}
              </div>
            )}

            {detail.rag.coverageComparison && (
              <div
                className={`rounded-md border px-3 py-2 text-sm ${
                  detail.rag.coverageComparison.pagesChunksMismatch
                    ? 'border-destructive/40 bg-destructive/5 text-destructive'
                    : 'border-border bg-muted/30'
                }`}
              >
                Pages↔chunks —{' '}
                {detail.rag.coverageComparison.distinctChunkPages ?? '—'} chunk pages
                {' / '}
                {detail.rag.coverageComparison.requiredPages ?? '—'} required
                {detail.rag.coverageComparison.expectedPages != null &&
                  ` (expected ${detail.rag.coverageComparison.expectedPages}`}
                {detail.rag.coverageComparison.failedPages != null &&
                  `, failed ${detail.rag.coverageComparison.failedPages}`}
                {detail.rag.coverageComparison.expectedPages != null && ')'}
                {detail.rag.coverageComparison.chunkCount != null &&
                  ` · ${detail.rag.coverageComparison.chunkCount} chunks`}
                {detail.rag.coverageComparison.pagesChunksMismatch
                  ? ' · mismatch'
                  : ' · ok'}
              </div>
            )}

            {detail.rag.error && (
              <p className='rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
                RAG detail unavailable: {detail.rag.error}
              </p>
            )}

            <div className='flex flex-wrap gap-2'>
              {detail.lexSignedSourceUrl && (
                <Button variant='outline' size='sm' asChild>
                  <a href={detail.lexSignedSourceUrl} target='_blank' rel='noreferrer'>
                    Lex OSS source
                  </a>
                </Button>
              )}
              {detail.rag.sentryTraceUrl && (
                <Button variant='outline' size='sm' asChild>
                  <a href={detail.rag.sentryTraceUrl} target='_blank' rel='noreferrer'>
                    Open trace
                  </a>
                </Button>
              )}
            </div>

            {detail.rag.ocrJob && (
              <div className='flex flex-wrap gap-2 text-xs text-muted-foreground'>
                {detail.rag.ocrJob.partial_result && (
                  <Badge variant='outline'>Partial result</Badge>
                )}
                {detail.rag.ocrJob.has_unrecovered_truncation && (
                  <Badge variant='outline'>Truncation</Badge>
                )}
                {detail.rag.ocrJob.truncation_severe && (
                  <Badge variant='destructive'>Severe truncation</Badge>
                )}
              </div>
            )}
          </header>

          <IngestionTimeline phases={detail.phases} />

          {pages.length > 0 && (
            <section className='space-y-3'>
              <h2 className='text-lg font-semibold'>Pages</h2>
              <div className='grid min-h-[520px] grid-cols-[minmax(0,480px)_minmax(0,1fr)] overflow-hidden rounded-lg border bg-card'>
                <div className='min-h-0 overflow-auto border-r'>
                  <Table>
                    <TableHeader className='sticky top-0 z-10 bg-card'>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>VLM</TableHead>
                        <TableHead>Cost</TableHead>
                        <TableHead>Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pages.map((page) => {
                        const isSelected = page.page_number === selectedPage
                        return (
                          <TableRow
                            key={page.page_number}
                            className={`cursor-pointer ${isSelected ? 'bg-accent' : ''}`}
                            onClick={() => selectPage(page.page_number)}
                          >
                            <TableCell>{page.page_number}</TableCell>
                            <TableCell>{page.status}</TableCell>
                            <TableCell>{page.page_type ?? '—'}</TableCell>
                            <TableCell>{page.n_vlm_calls ?? '—'}</TableCell>
                            <TableCell>
                              {page.cost_estimate_usd != null
                                ? `$${page.cost_estimate_usd.toFixed(4)}`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {page.duration_ms != null ? `${page.duration_ms} ms` : '—'}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className='min-h-0 min-w-0 overflow-auto p-4'>
                  {showSelectedPageInspector && (
                    <IngestionPageInspector
                      sourceId={sourceId}
                      pageNumber={selectedPage}
                      sourceUrl={sourceUrl}
                      sourceType={detail.source.type}
                      pageCount={sortedPageNumbers.length}
                      adjacentPageNumbers={adjacentPageNumbers}
                      onSelectAdjacentPage={selectPage}
                      ocrResultAvailable={Boolean(detail.rag.artifacts?.result_url)}
                      pageMeta={
                        selectedPageMeta
                          ? {
                              status: selectedPageMeta.status,
                              page_type: selectedPageMeta.page_type,
                              n_vlm_calls: selectedPageMeta.n_vlm_calls,
                              cost_estimate_usd: selectedPageMeta.cost_estimate_usd,
                              duration_ms: selectedPageMeta.duration_ms,
                            }
                          : undefined
                      }
                    />
                  )}
                  {!showSelectedPageInspector && (
                    <p className='text-sm text-muted-foreground'>
                      Select a page to inspect its render and extracted content.
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}
        </>
      )}
      </div>
    </div>
  )
}
